import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { fine, money, today } from '../../utils/format';
import { ErrorNotice } from '../../components/ui';
import { DateInput, Drawer, Field, NumberInput, TextInput } from '../../components/ui/Form';
import type { ChitDetail, Instalment } from './ChitsPage';

const PAY_MODES = ['CASH', 'UPI', 'CARD', 'BANK', 'CHEQUE'] as const;

interface BoardRow {
  metalCode: string;
  purityCode: string | null;
  rateType: string;
  currentRate: string | null;
}

interface Collected {
  receiptNo: string;
  instalmentNo: number;
  amount: string;
  weight: string | null;
  rate: string | null;
  matured: boolean;
}

/**
 * Taking an instalment.
 *
 * The next unpaid instalment is chosen for the cashier; on a flexible scheme
 * the amount can be changed within the scheme's range. On a gold-weight scheme
 * the grams the money buys at today's rate are shown before it is taken, so the
 * customer can be told exactly what went into their account. The receipt is
 * one tap away when it is done.
 */
export function CollectDrawer({
  account,
  onClose,
  onReceipt,
}: {
  account: ChitDetail & { instalments: Instalment[] };
  onClose: () => void;
  onReceipt: (instalmentNo: number) => void;
}) {
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<Collected | null>(null);

  const next = account.instalments.find((i) => i.status === 'DUE');
  const [f, setF] = useState({
    amount: next ? Number(next.due_amount) > 0 ? Number(next.due_amount).toFixed(2) : '' : '',
    paidOn: today(),
    payMode: 'CASH',
    referenceNo: '',
    remarks: '',
  });

  const isWeight = account.scheme_type === 'WEIGHT';
  const board = useQuery({
    queryKey: ['rate-board'],
    queryFn: () => api.get<{ data: BoardRow[] }>('/rates/board'),
    enabled: isWeight,
  });
  const rate = board.data?.data.find(
    (r) => r.rateType === 'SALE' && r.metalCode === account.metal_code && r.purityCode === account.purity_code,
  )?.currentRate;
  const grams = isWeight && rate && Number(f.amount) > 0 ? Number(f.amount) / Number(rate) : null;

  const fixed = !account.flexible && next && Number(next.due_amount) > 0;
  const outOfRange =
    account.flexible &&
    Number(f.amount) > 0 &&
    (Number(f.amount) < Number(account.min_amount) || (account.max_amount !== null && Number(f.amount) > Number(account.max_amount)));

  const save = useMutation({
    mutationFn: () =>
      api.post<Collected>(`/chit/accounts/${account.id}/instalments`, {
        paidOn: f.paidOn,
        payMode: f.payMode,
        amount: Number(f.amount).toFixed(2),
        ...(f.referenceNo.trim() ? { referenceNo: f.referenceNo.trim() } : {}),
        ...(f.remarks.trim() ? { remarks: f.remarks.trim() } : {}),
      }),
    onSuccess: (r) => {
      setDone(r);
      setError(null);
      for (const k of ['chit-account', 'chit-accounts', 'chit-overview', 'chit-due']) {
        void client.invalidateQueries({ queryKey: [k] });
      }
    },
    onError: setError,
  });

  if (!next && !done) {
    return (
      <Drawer title="Nothing due" onClose={onClose}>
        <p className="text-sm">Every instalment on {account.chit_no} is paid.</p>
      </Drawer>
    );
  }

  if (done) {
    return (
      <Drawer title="Instalment received" subtitle={account.customer_name} onClose={onClose}>
        <div className="space-y-4 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-success/10 text-2xl text-success">✓</div>
          <p className="num text-2xl font-bold">{money(done.amount)}</p>
          <p className="text-sm text-ink-soft">
            Instalment {done.instalmentNo} of {account.instalments.length} · receipt <span className="num">{done.receiptNo}</span>
          </p>
          {done.weight ? (
            <p className="text-sm">
              <span className="num font-semibold">{fine(done.weight)}</span> of gold at{' '}
              <span className="num">{money(done.rate)}</span>/g
            </p>
          ) : null}
          {done.matured ? (
            <p className="rounded-md bg-gold-50 px-3 py-2 text-sm font-medium text-gold-700">
              That was the last instalment — this account has matured and can be redeemed.
            </p>
          ) : null}
          <div className="flex gap-2">
            <button className="btn-primary flex-1" onClick={() => onReceipt(done.instalmentNo)}>
              Print receipt
            </button>
            <button className="btn-ghost flex-1" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </Drawer>
    );
  }

  const ready = Number(f.amount) > 0 && !outOfRange && (!isWeight || !!rate);

  return (
    <Drawer
      title="Take instalment"
      subtitle={`${account.customer_name} · ${account.chit_no}`}
      onClose={onClose}
      footer={
        <button className="btn-primary w-full" disabled={!ready || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : `Receive ${money(Number(f.amount || 0).toFixed(2))}`}
        </button>
      }
    >
      <ErrorNotice error={error} />
      <form autoComplete="off" onKeyDown={onEnter} onSubmit={(e) => e.preventDefault()} className="space-y-4">
        <div className="rounded-lg border border-silver-100 bg-silver-50 p-3 text-sm">
          <div className="flex justify-between">
            <span className="text-ink-soft">Instalment</span>
            <span className="num font-medium">
              {next!.instalment_no} of {account.instalments.length}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-soft">Due</span>
            <span className={`num ${next!.overdue ? 'font-medium text-danger' : ''}`}>
              {next!.due_date.slice(0, 10)}
              {next!.overdue ? ' · overdue' : ''}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-soft">Paid so far</span>
            <span className="num">{money(account.paid_amount)}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Payment date" required>
            <DateInput value={f.paidOn} onChange={(v) => setF({ ...f, paidOn: v })} />
          </Field>
          <Field
            label="Amount"
            required
            hint={
              fixed
                ? 'Fixed by the scheme'
                : account.flexible
                  ? `${money(account.min_amount)} – ${account.max_amount ? money(account.max_amount) : 'any'}`
                  : undefined
            }
            error={outOfRange ? 'Outside what this scheme allows.' : null}
          >
            <NumberInput value={f.amount} onChange={(v) => setF({ ...f, amount: v })} decimals={2} disabled={!!fixed} />
          </Field>
        </div>

        {isWeight ? (
          <div className="rounded-lg border border-gold-300 bg-gold-50 p-3 text-sm">
            {rate ? (
              <>
                <div className="flex justify-between">
                  <span>
                    Today’s {account.purity_code} rate
                  </span>
                  <span className="num">{money(rate)}/g</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Gold this buys</span>
                  <span className="num">{grams ? fine(grams.toFixed(4)) : '—'}</span>
                </div>
              </>
            ) : (
              <p className="text-danger">There is no rate for today. Publish today’s rate before taking a gold-weight instalment.</p>
            )}
          </div>
        ) : null}

        <Field label="Paid by">
          <div className="flex flex-wrap gap-2">
            {PAY_MODES.map((m) => (
              <button
                type="button"
                key={m}
                onClick={() => setF({ ...f, payMode: m })}
                className={`rounded-md border px-3 py-1.5 text-sm ${
                  f.payMode === m ? 'border-gold-500 bg-gold-50 font-medium text-gold-700' : 'border-silver-100 text-ink-soft'
                }`}
              >
                {m === 'BANK' ? 'Bank' : m.charAt(0) + m.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </Field>
        {f.payMode !== 'CASH' ? (
          <Field label={f.payMode === 'CHEQUE' ? 'Cheque number' : 'Reference / UTR'}>
            <TextInput value={f.referenceNo} onChange={(v) => setF({ ...f, referenceNo: v })} maxLength={64} />
          </Field>
        ) : null}
        <Field label="Remarks">
          <TextInput value={f.remarks} onChange={(v) => setF({ ...f, remarks: v })} maxLength={300} />
        </Field>
      </form>
    </Drawer>
  );
}
